<script lang="ts" setup>
import { ref } from 'vue'
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'
import { CameraIcon, FilmIcon } from '@heroicons/vue/24/outline'
import { useMessageStore } from '@src/stores/messages'
import { Toast } from '@src/plugins/toast'

const emit = defineEmits(['closeModal'])
const messageStore = useMessageStore()
const loading = ref(false)
const videoInputRef = ref<HTMLInputElement | null>(null)

function isCancellation(error: unknown): boolean {
    const msg = (error as any)?.message ?? ''
    return /cancel/i.test(msg) || /NO_PICTURE_TAKEN/i.test(msg) || /dismissed/i.test(msg)
}

async function ensureCameraPermission(): Promise<boolean> {
    try {
        let status = await Camera.checkPermissions()
        if (status.camera === 'granted') return true

        if (status.camera === 'denied') {
            Toast.fire('Permissão negada', 'Acesse Configurações > Aplicativos > dChat > Permissões e habilite a Câmera.', 'warning')
            return false
        }

        const result = await Camera.requestPermissions({ permissions: ['camera'] })
        if (result.camera !== 'granted') {
            Toast.fire('Permissão necessária', 'A permissão de câmera é necessária para esta função.', 'warning')
            return false
        }
        return true
    } catch {
        return true
    }
}

async function capturePhoto() {
    loading.value = true
    try {
        if (!await ensureCameraPermission()) return
        const result = await Camera.getPhoto({
            source: CameraSource.Camera,
            resultType: CameraResultType.Uri,
            quality: 90,
        })
        const response = await fetch(result.webPath!)
        const blob = await response.blob()
        const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' })
        await messageStore.submitMedia([file], 'image', ['jpg', 'jpeg', 'png'])
    } catch (error) {
        console.error('[AttachCamera] capturePhoto error:', error)
        if (!isCancellation(error)) {
            const msg = (error as any)?.message ?? String(error)
            Toast.fire('Erro ao capturar foto', msg, 'error')
        }
    } finally {
        loading.value = false
        emit('closeModal')
    }
}

function captureVideo() {
    videoInputRef.value?.click()
}

async function handleVideoFile(event: Event) {
    const target = event.target as HTMLInputElement
    const file = target.files?.[0]
    if (!file) {
        emit('closeModal')
        return
    }
    try {
        await messageStore.submitMedia([file], 'video', ['mp4', '3gp', 'mov'])
    } catch (error) {
        const msg = (error as any)?.message ?? String(error)
        Toast.fire('Erro ao enviar vídeo', msg, 'error')
    } finally {
        target.value = ''
        emit('closeModal')
    }
}
</script>

<template>
    <input
        ref="videoInputRef"
        accept="video/*"
        :capture="('camcorder' as any)"
        style="display:none"
        type="file"
        @change="handleVideoFile"
    >
    <div class="fixed z-10 inset-0" role="dialog" @click.stop @touchstart.stop @touchend.stop>
        <div class="fixed inset-0 bg-gray-500/25 dark:bg-gray-900/25"
             @click="$emit('closeModal')"
             @touchend.prevent="$emit('closeModal')"/>
        <div class="flex items-center justify-center min-h-screen px-4 text-center pointer-events-none">
            <div
                class="inline-block relative z-30 pointer-events-auto bg-white dark:bg-gray-800 rounded-xl text-left overflow-hidden shadow-xl transform transition-all w-72">
                <div class="bg-white dark:bg-gray-800 p-6">
                    <div class="flex items-center mb-5">
                        <CameraIcon class="w-6 h-6 mr-3 text-gray-900 dark:text-gray-100"/>
                        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Câmera</h3>
                    </div>
                    <div class="flex gap-3">
                        <button
                            :disabled="loading"
                            class="flex-1 flex flex-col items-center gap-2 p-4 rounded-xl bg-teal-50 hover:bg-teal-100 dark:bg-teal-900/30 dark:hover:bg-teal-900/50 transition-colors disabled:opacity-50"
                            @touchend.prevent="capturePhoto"
                            @click="capturePhoto">
                            <CameraIcon class="w-8 h-8 text-teal-700 dark:text-teal-400"/>
                            <span class="text-sm font-medium text-teal-700 dark:text-teal-400">Foto</span>
                        </button>
                        <button
                            :disabled="loading"
                            class="flex-1 flex flex-col items-center gap-2 p-4 rounded-xl bg-orange-50 hover:bg-orange-100 dark:bg-orange-900/30 dark:hover:bg-orange-900/50 transition-colors disabled:opacity-50"
                            @touchend.prevent="captureVideo"
                            @click="captureVideo">
                            <FilmIcon class="w-8 h-8 text-orange-700 dark:text-orange-400"/>
                            <span class="text-sm font-medium text-orange-700 dark:text-orange-400">Vídeo</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
